import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { deleteDataSource, previewDataSourceDeletion } from "../../../../api/dataSourceDeletionApi";

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function ImpactList({ title, lines, empty }) {
  return (
    <section className="admin-re-delete-impact">
      <h3>{title}</h3>
      {lines?.length ? (
        <ul>
          {lines.map((line) => (
            <li key={`${line.action}-${line.label}`}>
              <strong>{formatCount(line.count)}</strong> {line.label}
            </li>
          ))}
        </ul>
      ) : (
        <p>{empty}</p>
      )}
    </section>
  );
}

export default function DeleteDataSourceModal({ source, domain, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const sourceName = source?.displayName || source?.sourceName || "this source";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setPreview(null);
    setAcknowledged(false);
    previewDataSourceDeletion(source, domain)
      .then((result) => {
        if (!cancelled) setPreview(result.preview);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || "Unable to check what this delete would change.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source, domain]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !deleting) onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [deleting, onClose]);

  const handleDelete = async () => {
    if (!preview?.deletable || !acknowledged || deleting) return;
    try {
      setDeleting(true);
      setError("");
      const result = await deleteDataSource(source, domain);
      onDeleted?.(result);
    } catch (deleteError) {
      setError(deleteError.message || "Delete failed.");
      setDeleting(false);
    }
  };

  return createPortal(
    <div className="admin-re-modal-backdrop" role="presentation" onClick={() => !deleting && onClose?.()}>
      <div
        className="admin-re-modal panel-card admin-re-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-data-source-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Delete data source</p>
            <h2 id="delete-data-source-title">Delete {preview?.sourceName || sourceName}?</h2>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose} disabled={deleting}>
            Close
          </button>
        </header>
        <div className="admin-re-modal-body">
          {loading ? <p>Checking what this delete would change…</p> : null}
          {error ? <p className="admin-re-run-error">{error}</p> : null}
          {!loading && preview?.blockedReason ? <p>{preview.blockedReason}</p> : null}
          {!loading && preview?.deletable ? (
            <>
              <p>
                Deleting <strong>{preview.sourceName}</strong> removes it from Data Sources. This
                cannot be undone.
              </p>
              <ImpactList
                title="Removed with this source"
                lines={preview.removed}
                empty="Only the source record itself."
              />
              <ImpactList
                title="Left in place"
                lines={preview.retained}
                empty="No collected records stay behind."
              />
              {preview.notes?.length ? (
                <ul className="admin-re-delete-notes">
                  {preview.notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              ) : null}
              <label className="admin-re-delete-ack">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                I understand this permanently removes the source and the records listed above.
              </label>
            </>
          ) : null}
        </div>
        <footer className="admin-re-modal-footer">
          <button type="button" className="midpoint-share-button" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          {preview?.deletable ? (
            <button
              type="button"
              className="admin-btn admin-btn--danger"
              onClick={handleDelete}
              disabled={!acknowledged || deleting}
            >
              {deleting ? "Deleting…" : "Delete source"}
            </button>
          ) : null}
        </footer>
      </div>
    </div>,
    document.body
  );
}
